/**
 * Session state, held in one place.
 *
 * The permission list comes from the server and is never computed on the client.
 * `can()` drives what the UI offers, but it is presentation only — the API refuses
 * the call independently. A hidden button is not access control.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { ApiError, api } from "../../shared/api/client";
import type { Session } from "../../shared/api/types";

/**
 * Only ever a local module path. Validating this is not optional: `next` comes from
 * the query string, so accepting it blindly is an open redirect — an attacker sends
 * `/?next=https://evil.example`, the victim signs in to the real PetPoint, and is
 * then delivered to a convincing copy.
 *
 * A single leading slash followed by "apps/" is the whole allowed shape. That
 * rejects "//evil.example" (protocol-relative, and a very common miss), any absolute
 * URL, and any path outside a module.
 */
function safeNext(raw: string | null): string | null {
  if (!raw) return null;
  if (!/^\/apps\/[\w./-]*$/.test(raw)) return null;
  if (raw.startsWith("//")) return null;
  return raw;
}

interface SessionState {
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: string) => boolean;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setSession(await api.get<Session>("/auth/me"));
    } catch (err) {
      // A 401 on startup is the normal "not signed in yet" case, not an error
      // worth surfacing. Anything else is worth knowing about.
      if (!(err instanceof ApiError && err.isUnauthenticated)) {
        console.error("could not load session", err);
      }
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await api.post<Session>("/auth/login", { email, password });
    setSession(result);

    // If the gateway bounced a module page here because there was no session, go
    // back to it rather than dumping the person on the dashboard.
    const next = safeNext(new URLSearchParams(window.location.search).get("next"));
    if (next) {
      // A real navigation, not a router push: module pages are server-rendered by
      // their own service and are not client-side routes.
      window.location.replace(next);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      // Clear locally even if the call failed — the alternative is a UI that
      // claims you are still signed in when you asked not to be.
      setSession(null);
    }
  }, []);

  const can = useCallback(
    (permission: string) => session?.permissions.includes(permission) ?? false,
    [session],
  );

  const value = useMemo(
    () => ({ session, loading, signIn, signOut, refresh, can }),
    [session, loading, signIn, signOut, refresh, can],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSession must be used inside a SessionProvider");
  return ctx;
}
